// src/utils/gmail-connect.js
// Obtain a Google OAuth access token with Gmail scopes by re-consenting through the
// existing Firebase Google sign-in. Token is short-lived (~1h) and kept in memory +
// sessionStorage; the backend uses it per send/sync call. No secrets stored.
import firebase, { auth } from '../firebase/firebaseConfig';

const GMAIL_SCOPES = [
  'https://www.googleapis.com/auth/gmail.compose',
  'https://www.googleapis.com/auth/gmail.send'
];

const STORE_KEY = 'gmailAccessToken';
const STORE_EXP = 'gmailAccessTokenExp';

let inMemoryToken = null;

export function getStoredGmailToken() {
  if (inMemoryToken) return inMemoryToken;
  try {
    const token = sessionStorage.getItem(STORE_KEY);
    const exp = Number(sessionStorage.getItem(STORE_EXP) || 0);
    if (token && exp > Date.now()) {
      inMemoryToken = token;
      return token;
    }
  } catch (e) { /* ignore */ }
  return null;
}

export function isGmailConnected() {
  return !!getStoredGmailToken();
}

export function clearGmailToken() {
  inMemoryToken = null;
  try {
    sessionStorage.removeItem(STORE_KEY);
    sessionStorage.removeItem(STORE_EXP);
  } catch (e) { /* ignore */ }
}

// Triggers a Google popup requesting Gmail scopes. Returns the access token.
export async function connectGmail() {
  const provider = new firebase.auth.GoogleAuthProvider();
  GMAIL_SCOPES.forEach((s) => provider.addScope(s));
  provider.setCustomParameters({ prompt: 'consent', access_type: 'online' });

  const result = await auth.signInWithPopup(provider);
  const credential = result?.credential;
  const accessToken = credential?.accessToken;
  if (!accessToken) throw new Error('No Gmail access token returned. Did you grant the Gmail permissions?');

  inMemoryToken = accessToken;
  try {
    // Google OAuth access tokens last ~1h; expire ours a little early to be safe.
    sessionStorage.setItem(STORE_KEY, accessToken);
    sessionStorage.setItem(STORE_EXP, String(Date.now() + 50 * 60 * 1000));
  } catch (e) { /* ignore */ }
  return accessToken;
}

// Returns a valid token, prompting the connect flow if needed.
export async function ensureGmailToken() {
  return getStoredGmailToken() || (await connectGmail());
}
